// 客户端图片处理：压缩 + EXIF 移除

export interface CompressOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
  mimeType?: string
}

const DEFAULTS: Required<CompressOptions> = {
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.85,
  mimeType: 'image/jpeg',
}

function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('图片加载失败'))
    }
    img.src = url
  })
}

/**
 * 压缩图片到指定尺寸与质量，并自动剥离 EXIF（通过 canvas 重绘实现）。
 * 输出 Blob（默认 image/jpeg）。
 */
export async function compressImage(
  file: File | Blob,
  options: CompressOptions = {}
): Promise<Blob> {
  const opts = { ...DEFAULTS, ...options }
  const img = await loadImage(file)

  let { width, height } = img
  const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height, 1)
  width = Math.round(width * ratio)
  height = Math.round(height * ratio)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 不可用')

  // 用白色填充（处理 PNG 透明背景转 JPEG 时的黑边问题）
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, width, height)
  ctx.drawImage(img, 0, 0, width, height)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('压缩失败'))
      },
      opts.mimeType,
      opts.quality
    )
  })
}

/**
 * 读取一个 File / Blob 为 base64 data URL（用于 AI 视觉模型输入或本地预览）。
 * 主要给后端 AI 路径使用；上传时用 compressImage + FormData 更高效。
 */
export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('读取失败'))
    reader.readAsDataURL(blob)
  })
}
